import { html, HTMLTemplateResult, nothing, TemplateResult } from "lit";
import { ScheduleForm } from "./scheduleForm";
import { CheckboxChangeEvent, FieldInputEvent, ScheduleAction } from "@lib/types";
import { SCHEDULE_RECURRENCE_TO_READABLE, ScheduleRecurrence } from "../../types";
import "../inputField/inputField";
import moment from "moment";

/** Gets value input template. */
function getValueInput(this: ScheduleForm): HTMLTemplateResult {
    return html`
        ${this.form.field(
            {
                name: "value",
            },
            field => {
                const { parameterValueType } = this;
                if (parameterValueType === "bool" || this.form.api.state.values.action === ScheduleAction.Impulse) {
                    return html`
                        <fw-select
                            class="input"
                            label="Значение"
                            .value=${field.state.value}
                            .allowDeselect=${false}
                            @fwChange="${(event: FieldInputEvent<"true" | "false">) => {
                                const newValue = event.detail.value;
                                field.handleChange(newValue);
                            }}"
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            .disabled=${this.form.api.state.values.action === ScheduleAction.Impulse}
                        >
                            <fw-select-option .value=${"true"}>True</fw-select-option>
                            <fw-select-option .value=${"false"}>False</fw-select-option>
                        </fw-select>
                    `;
                } else if (parameterValueType === "enum") {
                    return html`
                        <fw-select
                            class="input"
                            label="Значение"
                            .value=${field.state.value}
                            .allowDeselect=${false}
                            @fwChange="${(event: FieldInputEvent<string>) => {
                                const newValue = event.detail.value;
                                field.handleChange(newValue);
                            }}"
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                        >
                            ${this.parameterValueEnumOptions.map(
                                option => html`
                                    <fw-select-option .value=${option.value}>${option.label}</fw-select-option>
                                `,
                            )}
                        </fw-select>
                    `;
                }
                return html`
                    <fw-input
                        class="input"
                        type="text"
                        label="Значение"
                        .value="${field.state.value}"
                        error-text=${field.state.meta.errors
                            .map((error: { message: string }) => error.message)
                            .join(",")}
                        state=${field.state.meta.errors.length > 0 ?
                            "error" :
                            "normal"}
                        @fwInput="${(event: FieldInputEvent) => {
                            const newValue = event.detail.value;
                            field.handleChange(newValue);
                        }}"
                    ></fw-input>
                `;
            },
        )}
    `;
}

/** Schedule form template. */
// No point in splitting template function into smaller functions.
// eslint-disable-next-line max-lines-per-function
export function scheduleFormTemplate(this: ScheduleForm): TemplateResult {
    return html`
        <form class="form" @submit=${this.handleSubmit.bind(this)}>
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "name",
                    },
                    field => html`
                        <fw-input
                            class="input"
                            type="text"
                            placeholder="Текст"
                            label="Название"
                            .value="${field.state.value}"
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            @fwInput="${(event: FieldInputEvent) => {
                                const newValue = event.detail.value;
                                field.handleChange(newValue);
                            }}"
                        ></fw-input>
                    `,
                )}
                ${this.form.field(
                    {
                        name: "color",
                    },
                    field => html`
                        <ms-colorpicker
                            class=${(field.form.getFieldMeta("name")?.errors?.length ?? 0) > 0 ?
                                "colorpicker form-row-button-center" :
                                "colorpicker"}
                            style="position: relative"
                            colorselect="${field.state.value}"
                            @colorChange=${(event: CustomEvent<{ color: string }>) => {
                                const { color } = event.detail;
                                field.handleChange(color);
                            }}
                        ></ms-colorpicker>
                    `,
                )}
            </div>
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "priority",
                    },
                    field => html`
                        <fw-input
                            class="input"
                            type="number"
                            placeholder="1"
                            min="1"
                            label="Приоритет"
                            .value="${field.state.value}"
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            @fwInput="${(event: FieldInputEvent) => {
                                const newValue = event.detail.value;
                                field.handleChange(parseInt(newValue, 10));
                            }}"
                        ></fw-input>
                    `,
                )}
            </div>
            ${this.form.field(
                {
                    name: "dateTime",
                },
                () => { /* We don`t need to render anything. This initializes the field, giving us access to its instance. */ },
            )}
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "date",
                    },
                    field => html`
                        <fw-datepicker
                            class="input datepicker"
                            full-width
                            placeholder="01.01.2025"
                            label="Дата"
                            update-text="Применить"
                            .hoistTooltip=${false}
                            .value="${moment(field.state.value, "DD.MM.YYYY").format("YYYY-MM-DD")}"
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => {
                                    const errorMessage = typeof error === "string" ?
                                        error :
                                        error.message;
                                    return errorMessage;
                                })
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            @fwChange="${(event: FieldInputEvent) => {
                                let newValue = event.detail.value;
                                const isoParse = moment(newValue, moment.ISO_8601);
                                if (isoParse.isValid()) {
                                    newValue = isoParse.format("DD.MM.YYYY");
                                }
                                this.handleDateChange(newValue);
                            }}"
                            display-format="dd.MM.yyyy"
                        ></fw-datepicker>
                    `,
                )}
            </div>
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "time",
                    },
                    field => html`
                        <fw-input
                            class="input"
                            type="time"
                            step="1"
                            placeholder="08:00:00"
                            label="Время"
                            .value="${field.state.value}"
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            @fwInput="${(event: FieldInputEvent) => {
                                const newValue = event.detail.value;
                                this.handleTimeChange(newValue);
                            }}"
                        ></fw-input>
                    `,
                )}
            </div>
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "action",
                    },
                    field => html`
                        <fw-select
                            class="input"
                            label="Действие"
                            .value=${field.state.value}
                            placeholder="Действие"
                            .allowDeselect=${false}
                            @fwChange="${(event: FieldInputEvent<ScheduleAction>) => {
                                const newValue = event.detail.value;
                                field.handleChange(newValue);
                                if (newValue === ScheduleAction.Impulse) {
                                    this.form.api.getFieldInfo("value").instance?.handleChange("true");
                                } else if (this.parameterValueType !== "bool") {
                                    this.form.api.getFieldInfo("value").instance?.handleChange("");
                                }
                            }}"
                        >
                            <fw-select-option .value=${ScheduleAction.Impulse}>Выдать импульс</fw-select-option>
                            <fw-select-option .value=${ScheduleAction.SetParameter}
                                >Установить параметр</fw-select-option
                            >
                        </fw-select>
                    `,
                )}
            </div>
            ${this.form.field(
                {
                    name: "parameterId",
                },
                () => { /* We don`t need to render anything. This initializes the field, giving us access to its instance. */ },
            )}
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "parameter",
                    },
                    field => html`
                        <ms4-input-field
                            class="input"
                            type="text"
                            label="Параметр"
                            .value="${field.state.value}"
                            .errorText=${(field.state.meta.errors as ({ message: string } | string)[])
                                .map(error => {
                                    if (typeof error === "string") {
                                        return error;
                                    }
                                    return error.message;
                                })
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            disabled
                            @fwInput="${(event: FieldInputEvent) => {
                                const newValue = event.detail.value;
                                field.handleChange(newValue);
                            }}"
                        ></ms4-input-field>
                        <fw-button
                            id="edit-parameter-button"
                            size="icon"
                            @click=${this.handleEditParameterButtonClick.bind(this)}
                            class=${field.state.meta.errors.length > 0 ?
                                "form-row-button-center" :
                                ""}
                        >
                            <fw-icon name="edit" size="20"></fw-icon>
                        </fw-button>
                    `,
                )}
            </div>
            ${this.form.api.state.values.action === ScheduleAction.Impulse ?
                html`
                      <div class="form-row">
                          ${this.form.field(
                                {
                                    name: "impulseDuration",
                                },
                                field => html`
                                  <fw-input
                                      class="input"
                                      type="time"
                                      step="0.001"
                                      placeholder="08:00:00"
                                      label="Длительность импульса"
                                      error-text=${field.state.meta.errors
                                            .map((error: { message: string }) => error.message)
                                            .join(",")}
                                      state=${field.state.meta.errors.length > 0 ?
                                            "error" :
                                            "normal"}
                                      .value="${this.formatImpulseDuration(field.state.value)}"
                                      @fwInput="${(event: FieldInputEvent) => {
                                            const newValue = event.detail.value;
                                            this.handleImpulseDurationChange(newValue);
                                        }}"
                                  ></fw-input>
                              `,
                            )}
                      </div>
                  ` :
                nothing}
            <div class="form-row">${getValueInput.call(this)}</div>
            ${this.isEdit ?
                this.form.field(
                    {
                        name: "isEnabled",
                    },
                    field => html`
                          <fw-checkbox
                              .checked=${field.state.value}
                              @fwChange=${(event: CheckboxChangeEvent) => {
                                    this.handleActivationChange(event);
                                }}
                          >
                              ${this.form.api.getFieldValue("recurrenceRule.recurrence") ===
                              ScheduleRecurrence.NoRepeat || this.isSingle ?
                                    "Активировать событие" :
                                    "Активировать ряд событий"}
                          </fw-checkbox>
                      `,
                ) :
                nothing}
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "recurrenceRule.recurrence",
                    },
                    field => html`
                        <fw-input
                            class="input"
                            type="text"
                            label="Повтор"
                            placeholder="Повтор"
                            .value=${SCHEDULE_RECURRENCE_TO_READABLE[field.state.value]}
                            .allowDeselect=${false}
                            .disabled=${true}
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            @fwInput="${(event: FieldInputEvent<ScheduleRecurrence>) => {
                                this.handleRecurrenceRuleChange(event);
                            }}"
                        >
                        </fw-input>
                        <fw-button
                            id="edit-repeat-button"
                            size="icon"
                            color="primary"
                            class=${field.state.meta.errors.length > 0 ?
                                "form-row-button-center" :
                                ""}
                            @click=${this.handleEditRepeatButtonClick.bind(this)}
                        >
                            <fw-icon name="edit" size="20"></fw-icon>
                        </fw-button>
                    `,
                )}
            </div>
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "comment",
                    },
                    field => html`
                        <fw-input
                            class="input"
                            type="text"
                            placeholder="Текст"
                            label="Комментарий"
                            .value=${field.state.value}
                            error-text=${field.state.meta.errors
                                .map((error: { message: string }) => error.message)
                                .join(",")}
                            state=${field.state.meta.errors.length > 0 ?
                                "error" :
                                "normal"}
                            @fwInput="${(event: FieldInputEvent) => {
                                const newValue = event.detail.value;
                                field.handleChange(newValue);
                            }}"
                        ></fw-input>
                    `,
                )}
            </div>
            <div class="form-row">
                ${this.form.field(
                    {
                        name: "writeToArchive",
                    },
                    field => html`
                        <fw-checkbox
                            .checked=${field.state.value}
                            @fwChange=${(event: CheckboxChangeEvent) => {
                                field.handleChange(event.detail.meta.checked);
                            }}
                        >
                            Записать в архив сообщений
                        </fw-checkbox>
                    `,
                )}
            </div>
            <div>
                ${this.form.api.state.values.writeToArchive ?
                    html` ${this.form.field(
                        {
                            name: "archiveMessage",
                        },
                        field => html`
                              <fw-input
                                  class="input"
                                  type="text"
                                  placeholder="Текст"
                                  label="Сообщение"
                                  .value=${field.state.value}
                                  error-text=${field.state.meta.errors
                                        .map((error: { message: string }) => error.message)
                                        .join(",")}
                                  state=${field.state.meta.errors.length > 0 ?
                                        "error" :
                                        "normal"}
                                  @fwInput="${(event: FieldInputEvent) => {
                                        const newValue = event.detail.value;
                                        field.handleChange(newValue);
                                    }}"
                              ></fw-input>
                          `,
                    )}` :
                    nothing}
            </div>
            <div class="actions">
                <fw-button type="submit" class="submit-button">Применить</fw-button>
                ${this.isEdit ?
                    html`<fw-button class="delete-button" type="button" @click=${this.handleDeleteButtonClick.bind(this)}
                          >Удалить</fw-button
                      >` :
                    nothing}
                <fw-button type="button" color="secondary" @click=${this.handleCancelButtonClick.bind(this)}>Отмена</fw-button>
            </div>
        </form>
    `;
}
